import React, { useEffect, useState } from 'react';
import dateFormat from 'dateformat';
import {
  Dialog,
  DialogActions,
  Typography,
  Button,
  useMediaQuery,
  IconButton,
  Tooltip
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { formatter } from '#component/shipment/helper';
import { fetchDeviceDetails, clearDeviceDetails } from '#action/device/device-details';
import PreloaderComponent from '#component/preloader';
import { isActiveDevice } from '#component/device/helper';
import { downloadFile } from '#action/download';
import {
  StyledLink,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogContentList,
  ProjectsTitle,
  DialogContentLoading,
  CalibrationCertificateContainer,
  CertificateTitle,
  CertificateTitleBold,
  DownloadBtnAndDate,
  DateDownload,
  Overlay
} from './styles';
import renderItem from './render-item';
import { getDateTimeFormat } from '#helper';

/**
 * Renders dialog with all projects related to device.
 * @returns {React.FC}
 */

const DeviceProjectsTreePopup = props => {
  const {
    // TODO: why do we need `deviceUid` and `deviceTitle` if we have the `device`?
    // `setSelectedDevice` seems useless.
    deviceUid,
    deviceTitle,
    open,
    setOpen,
    setSelectedDevice
  } = props;
  const dispatch = useDispatch();
  const [init, setInit] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(
    theme.breakpoints.down(theme.breakpoints.values.XGA)
  );
  const account = useSelector(s => s.account, shallowEqual);
  const deviceDetails = useSelector(s => s.deviceDetails, shallowEqual);
  const device = deviceDetails?.device || {};
  const { uid, shipments, appliances } = device;

  const isActive = isActiveDevice(device);
  const dateFormatter = (date, userAccount) => {
    const timestamp = Date.parse(date);
    if (!date || Number.isNaN(timestamp)) return null;

    return dateFormat(
      date,
      getDateTimeFormat(userAccount?.measurementSystem, 'dateShort')
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchDeviceDetails(deviceUid, true));
      setInit(true);
    };

    fetchData();

    return () => {
      dispatch(clearDeviceDetails());
    };
  }, [dispatch, deviceUid]);

  const handleClose = () => {
    setOpen(false);
    setSelectedDevice?.({});
  };

  const handleDialogClick = event => {
    event.stopPropagation();
  };

  const handleDialogMouseDown = event => {
    event.stopPropagation();
  };

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      await dispatch(downloadFile(
        `device/distinct/${device.id}/calibration-certificate`,
        `calibration-certificate-${deviceUid}.pdf`
      ));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      onClick={handleDialogClick}
      onMouseDown={handleDialogMouseDown}
    >
      {!init ?
        <DialogContentLoading>
          <PreloaderComponent />
        </DialogContentLoading> :
        <>
          <StyledDialogTitle>
            Details
            <br />
            <Typography variant="body1" component="span">
              Device:&nbsp;
              {deviceTitle || uid}
            </Typography>
            {isActive && (
              <>
                {shipments?.length && (
                  <Typography variant="body1" component="p">
                    Active in shipment
                    {shipments.length > 1 ? 's' : ''}
                    :&nbsp;
                    {shipments.map((s, i) => (
                      <React.Fragment key={`shipment${s?.uid}`}>
                        <StyledLink to={`/shipment/${s.id}`}>
                          {s.label ?? formatter(s?.uid)}
                        </StyledLink>
                        {i + 1 !== shipments.length ? ', ' : ''}
                      </React.Fragment>
                    ))}
                  </Typography>
                )}
                {appliances?.length && (
                  <Typography variant="body1" component="p">
                    Active in application
                    {appliances.length > 1 ? 's' : ''}
                    :&nbsp;
                    {appliances.map((s, i) => (
                      <React.Fragment key={`application${s?.id}`}>
                        <StyledLink to={`/warehouse/appliance-overview/${s.id}`}>
                          {s.label}
                        </StyledLink>
                        {i + 1 !== appliances.length ? ', ' : ''}
                      </React.Fragment>
                    ))}
                  </Typography>
                )}
              </>
            )}
          </StyledDialogTitle>
          <StyledDialogContent>
            <StyledDialogContentList>
              <ProjectsTitle variant="body1" component="p">
                Assigned to projects
              </ProjectsTitle>
              {renderItem(deviceDetails.device.projectsDetails, [], account, isMobile)}
            </StyledDialogContentList>
          </StyledDialogContent>
          {device.lastCalibration !== undefined && (
            <StyledDialogContent>
              <CalibrationCertificateContainer>
                <CertificateTitleBold variant="body1" component="p">
                  Calibration certificate
                </CertificateTitleBold>
              </CalibrationCertificateContainer>
              <CalibrationCertificateContainer>
                <CertificateTitle>Last calibration:</CertificateTitle>

                {device.lastCalibration !== null ? (
                  isDownloading ? (
                    <Overlay>
                      <PreloaderComponent />
                    </Overlay>
                  ) : (
                    <DownloadBtnAndDate blurred={isDownloading}>
                      <Tooltip title="Download">
                        <IconButton
                          aria-label="download-certificate"
                          onClick={handleDownload}
                          size="small"
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <DateDownload
                        onClick={handleDownload}
                        aria-label="download-date"
                      >
                        {dateFormatter(device.lastCalibration.date, account)}
                      </DateDownload>
                    </DownloadBtnAndDate>
                  )
                ) : (
                  <Typography>Not available</Typography>
                )}
              </CalibrationCertificateContainer>
            </StyledDialogContent>
          )}
          <DialogActions>
            <Button variant="outlined" color="primary" onClick={handleClose}>
              Close
            </Button>
          </DialogActions>
        </>}
    </Dialog>
  );
};

export default DeviceProjectsTreePopup;
